export type Rect = { x: number; y: number; width: number; height: number };

const TILE_ASPECT = 16 / 9;

export function gridLayout(count: number, area: Rect, gap: number): Rect[] {
    if (count === 0) return [];
    let best = { columns: 1, rows: count, width: 0 };
    for (let columns = 1; columns <= count; columns++) {
        const rows = Math.ceil(count / columns);
        const width = Math.min(
            (area.width - gap * (columns - 1)) / columns,
            ((area.height - gap * (rows - 1)) / rows) * TILE_ASPECT
        );
        if (width > best.width) best = { columns, rows, width };
    }
    const { columns, rows, width } = best;
    const height = width / TILE_ASPECT;
    const top = area.y + (area.height - (rows * height + (rows - 1) * gap)) / 2;
    return Array.from({ length: count }, (_, index) => {
        const row = Math.floor(index / columns);
        const inRow = row === rows - 1 ? count - row * columns : columns;
        const left = area.x + (area.width - (inRow * width + (inRow - 1) * gap)) / 2;
        return { x: left + (index % columns) * (width + gap), y: top + row * (height + gap), width, height };
    });
}

export function stageLayout(stripCount: number, area: Rect, gap: number): { stage: Rect; strip: Rect[] } {
    if (stripCount === 0) return { stage: area, strip: [] };
    const stripWidth = Math.round(area.width * 0.22);
    const stage = { x: area.x, y: area.y, width: area.width - stripWidth - gap, height: area.height };
    const tileHeight = stripWidth / TILE_ASPECT;
    const count = Math.min(stripCount, stripCapacity(area, gap));
    const top = area.y + (area.height - (count * tileHeight + (count - 1) * gap)) / 2;
    const strip = Array.from({ length: count }, (_, index) => ({
        x: stage.x + stage.width + gap,
        y: top + index * (tileHeight + gap),
        width: stripWidth,
        height: tileHeight,
    }));
    return { stage, strip };
}

export function stripCapacity(area: Rect, gap: number): number {
    const tileHeight = Math.round(area.width * 0.22) / TILE_ASPECT;
    return Math.max(1, Math.floor((area.height + gap) / (tileHeight + gap)));
}

export function fitRect(source: { width: number; height: number }, target: Rect, mode: 'cover' | 'contain') {
    const scale =
        mode === 'cover'
            ? Math.max(target.width / source.width, target.height / source.height)
            : Math.min(target.width / source.width, target.height / source.height);
    const width = source.width * scale;
    const height = source.height * scale;
    return { x: target.x + (target.width - width) / 2, y: target.y + (target.height - height) / 2, width, height };
}
